import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { API_URL } from './AdminShared';
import { Plus, Minus, Trash2, XCircle, CheckCircle } from 'lucide-react';

export const LandingContentTab = () => {
  const [content, setContent] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loadingContent, setLoadingContent] = useState(true);

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/landing-content`);
      setContent(res.data);
    } catch (error) {
      toast.error('Failed to load landing page content');
    } finally {
      setLoadingContent(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/admin/landing-content`, content, {
        headers: { 'Content-Type': 'application/json' }
      });
      toast.success('Landing page content updated! Changes are live.');
    } catch (error) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const updateField = (path, value) => {
    setContent(prev => {
      const copy = JSON.parse(JSON.stringify(prev));
      const keys = path.split('.');
      let obj = copy;
      for (let i = 0; i < keys.length - 1; i++) {
        if (!isNaN(keys[i])) keys[i] = parseInt(keys[i]);
        obj = obj[keys[i]];
      }
      const lastKey = !isNaN(keys[keys.length - 1]) ? parseInt(keys[keys.length - 1]) : keys[keys.length - 1];
      obj[lastKey] = value;
      return copy;
    });
  };

  const addPoint = (path) => {
    setContent(prev => {
      const copy = JSON.parse(JSON.stringify(prev));
      const keys = path.split('.');
      let obj = copy;
      for (const k of keys) obj = obj[!isNaN(k) ? parseInt(k) : k];
      obj.push('New point');
      return copy;
    });
  };

  const removePoint = (path, index) => {
    setContent(prev => {
      const copy = JSON.parse(JSON.stringify(prev));
      const keys = path.split('.');
      let obj = copy;
      for (const k of keys) obj = obj[!isNaN(k) ? parseInt(k) : k];
      obj.splice(index, 1);
      return copy;
    });
  };

  if (loadingContent) {
    return <div className="flex justify-center py-12"><div className="w-8 h-8 border-2 border-[#E07A3A] border-t-transparent rounded-full animate-spin" /></div>;
  }

  if (!content?.seller_section) return <p className="text-slate-500">No content found.</p>;

  const s = content.seller_section;

  return (
    <div className="space-y-8">
      {/* Section Toggle */}
      <div className="luxury-card p-6 flex items-center justify-between">
        <div>
          <h3 className="font-['Playfair_Display'] text-xl text-[#1A1A1A] font-semibold">Seller Value Section</h3>
          <p className="text-sm text-slate-500 mt-1">Toggle visibility of the seller comparison section on the landing page</p>
        </div>
        <button
          onClick={() => updateField('seller_section.enabled', !s.enabled)}
          className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors ${s.enabled ? 'bg-[#E07A3A]' : 'bg-slate-300'}`}
          data-testid="landing-toggle-seller"
        >
          <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${s.enabled ? 'translate-x-8' : 'translate-x-1'}`} />
        </button>
      </div>

      {/* Headlines */}
      <div className="luxury-card p-6">
        <h3 className="font-['Playfair_Display'] text-xl text-[#1A1A1A] font-semibold mb-4">Headlines</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-xs tracking-wider uppercase text-slate-500 mb-1 font-medium">Main Headline</label>
            <input
              type="text"
              value={s.headline}
              onChange={e => updateField('seller_section.headline', e.target.value)}
              className="w-full border border-slate-200 px-4 py-2.5 text-[#0F172A] focus:border-[#E07A3A] focus:outline-none"
              data-testid="landing-headline"
            />
          </div>
          <div>
            <label className="block text-xs tracking-wider uppercase text-slate-500 mb-1 font-medium">Accent Headline (italic, orange)</label>
            <input
              type="text"
              value={s.headline_accent}
              onChange={e => updateField('seller_section.headline_accent', e.target.value)}
              className="w-full border border-slate-200 px-4 py-2.5 text-[#0F172A] focus:border-[#E07A3A] focus:outline-none"
              data-testid="landing-headline-accent"
            />
          </div>
        </div>
      </div>

      {/* Traditional Agent */}
      <div className="luxury-card p-6">
        <h3 className="font-['Playfair_Display'] text-xl text-[#1A1A1A] font-semibold mb-4">Traditional Agent (Left Column)</h3>
        <div className="grid md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-xs tracking-wider uppercase text-slate-500 mb-1 font-medium">Label</label>
            <input type="text" value={s.traditional.label} onChange={e => updateField('seller_section.traditional.label', e.target.value)}
              className="w-full border border-slate-200 px-4 py-2.5 text-[#0F172A] focus:border-[#E07A3A] focus:outline-none" data-testid="landing-trad-label" />
          </div>
          <div>
            <label className="block text-xs tracking-wider uppercase text-slate-500 mb-1 font-medium">Price Display</label>
            <input type="text" value={s.traditional.price} onChange={e => updateField('seller_section.traditional.price', e.target.value)}
              className="w-full border border-slate-200 px-4 py-2.5 text-[#0F172A] focus:border-[#E07A3A] focus:outline-none" data-testid="landing-trad-price" />
          </div>
        </div>
        <div className="mb-4">
          <label className="block text-xs tracking-wider uppercase text-slate-500 mb-1 font-medium">Price Note</label>
          <input type="text" value={s.traditional.price_note} onChange={e => updateField('seller_section.traditional.price_note', e.target.value)}
            className="w-full border border-slate-200 px-4 py-2.5 text-[#0F172A] focus:border-[#E07A3A] focus:outline-none" data-testid="landing-trad-note" />
        </div>
        <label className="block text-xs tracking-wider uppercase text-slate-500 mb-2 font-medium">Comparison Points (red X)</label>
        {s.traditional.points.map((point, i) => (
          <div key={`point-${i}`} className="flex gap-2 mb-2">
            <XCircle className="h-4 w-4 text-red-400 mt-3 flex-shrink-0" />
            <input type="text" value={point} onChange={e => updateField(`seller_section.traditional.points.${i}`, e.target.value)}
              className="flex-1 border border-slate-200 px-3 py-2 text-sm text-[#0F172A] focus:border-[#E07A3A] focus:outline-none" data-testid={`landing-trad-point-${i}`} />
            <button onClick={() => removePoint('seller_section.traditional.points', i)} className="text-red-400 hover:text-red-600 p-2"><Minus className="h-4 w-4" /></button>
          </div>
        ))}
        <button onClick={() => addPoint('seller_section.traditional.points')} className="flex items-center gap-1 text-sm text-[#E07A3A] hover:underline mt-1" data-testid="landing-trad-add-point">
          <Plus className="h-3 w-3" /> Add point
        </button>
      </div>

      {/* Shopperden Column */}
      <div className="luxury-card p-6">
        <h3 className="font-['Playfair_Display'] text-xl text-[#1A1A1A] font-semibold mb-4">Shopperden (Right Column)</h3>
        <div className="grid md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-xs tracking-wider uppercase text-slate-500 mb-1 font-medium">Label</label>
            <input type="text" value={s.shopperden.label} onChange={e => updateField('seller_section.shopperden.label', e.target.value)}
              className="w-full border border-slate-200 px-4 py-2.5 text-[#0F172A] focus:border-[#E07A3A] focus:outline-none" data-testid="landing-sd-label" />
          </div>
          <div>
            <label className="block text-xs tracking-wider uppercase text-slate-500 mb-1 font-medium">Price Display</label>
            <input type="text" value={s.shopperden.price} onChange={e => updateField('seller_section.shopperden.price', e.target.value)}
              className="w-full border border-slate-200 px-4 py-2.5 text-[#0F172A] focus:border-[#E07A3A] focus:outline-none" data-testid="landing-sd-price" />
          </div>
        </div>
        <div className="mb-4">
          <label className="block text-xs tracking-wider uppercase text-slate-500 mb-1 font-medium">Price Note</label>
          <input type="text" value={s.shopperden.price_note} onChange={e => updateField('seller_section.shopperden.price_note', e.target.value)}
            className="w-full border border-slate-200 px-4 py-2.5 text-[#0F172A] focus:border-[#E07A3A] focus:outline-none" data-testid="landing-sd-note" />
        </div>
        <label className="block text-xs tracking-wider uppercase text-slate-500 mb-2 font-medium">Comparison Points (green check)</label>
        {s.shopperden.points.map((point, i) => (
          <div key={`point-${i}`} className="flex gap-2 mb-2">
            <CheckCircle className="h-4 w-4 text-emerald-400 mt-3 flex-shrink-0" />
            <input type="text" value={point} onChange={e => updateField(`seller_section.shopperden.points.${i}`, e.target.value)}
              className="flex-1 border border-slate-200 px-3 py-2 text-sm text-[#0F172A] focus:border-[#E07A3A] focus:outline-none" data-testid={`landing-sd-point-${i}`} />
            <button onClick={() => removePoint('seller_section.shopperden.points', i)} className="text-red-400 hover:text-red-600 p-2"><Minus className="h-4 w-4" /></button>
          </div>
        ))}
        <button onClick={() => addPoint('seller_section.shopperden.points')} className="flex items-center gap-1 text-sm text-[#E07A3A] hover:underline mt-1" data-testid="landing-sd-add-point">
          <Plus className="h-3 w-3" /> Add point
        </button>
      </div>

      {/* Savings Highlight */}
      <div className="luxury-card p-6">
        <h3 className="font-['Playfair_Display'] text-xl text-[#1A1A1A] font-semibold mb-4">Savings Highlight Banner</h3>
        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs tracking-wider uppercase text-slate-500 mb-1 font-medium">Amount</label>
            <input type="text" value={s.savings.amount} onChange={e => updateField('seller_section.savings.amount', e.target.value)}
              className="w-full border border-slate-200 px-4 py-2.5 text-[#0F172A] focus:border-[#E07A3A] focus:outline-none" data-testid="landing-savings-amount" />
          </div>
          <div>
            <label className="block text-xs tracking-wider uppercase text-slate-500 mb-1 font-medium">Title</label>
            <input type="text" value={s.savings.title} onChange={e => updateField('seller_section.savings.title', e.target.value)}
              className="w-full border border-slate-200 px-4 py-2.5 text-[#0F172A] focus:border-[#E07A3A] focus:outline-none" data-testid="landing-savings-title" />
          </div>
          <div>
            <label className="block text-xs tracking-wider uppercase text-slate-500 mb-1 font-medium">Subtitle</label>
            <input type="text" value={s.savings.subtitle} onChange={e => updateField('seller_section.savings.subtitle', e.target.value)}
              className="w-full border border-slate-200 px-4 py-2.5 text-[#0F172A] focus:border-[#E07A3A] focus:outline-none" data-testid="landing-savings-subtitle" />
          </div>
        </div>
      </div>

      {/* Three Pillars */}
      <div className="luxury-card p-6">
        <h3 className="font-['Playfair_Display'] text-xl text-[#1A1A1A] font-semibold mb-4">Three Pillars</h3>
        {s.pillars.map((pillar, i) => (
          <div key={`item-${i}`} className="border border-slate-100 p-4 mb-4">
            <label className="block text-xs tracking-wider uppercase text-slate-500 mb-1 font-medium">Pillar {i + 1} Title</label>
            <input type="text" value={pillar.title} onChange={e => updateField(`seller_section.pillars.${i}.title`, e.target.value)}
              className="w-full border border-slate-200 px-4 py-2.5 text-[#0F172A] focus:border-[#E07A3A] focus:outline-none mb-3" data-testid={`landing-pillar-${i}-title`} />
            <label className="block text-xs tracking-wider uppercase text-slate-500 mb-1 font-medium">Description</label>
            <textarea value={pillar.description} onChange={e => updateField(`seller_section.pillars.${i}.description`, e.target.value)}
              rows={3} className="w-full border border-slate-200 px-4 py-2.5 text-sm text-[#0F172A] focus:border-[#E07A3A] focus:outline-none" data-testid={`landing-pillar-${i}-desc`} />
          </div>
        ))}
      </div>

      {/* CTA */}
      <div className="luxury-card p-6">
        <h3 className="font-['Playfair_Display'] text-xl text-[#1A1A1A] font-semibold mb-4">Call to Action Button</h3>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs tracking-wider uppercase text-slate-500 mb-1 font-medium">Button Text</label>
            <input type="text" value={s.cta_text} onChange={e => updateField('seller_section.cta_text', e.target.value)}
              className="w-full border border-slate-200 px-4 py-2.5 text-[#0F172A] focus:border-[#E07A3A] focus:outline-none" data-testid="landing-cta-text" />
          </div>
          <div>
            <label className="block text-xs tracking-wider uppercase text-slate-500 mb-1 font-medium">Subtitle Below Button</label>
            <input type="text" value={s.cta_subtitle} onChange={e => updateField('seller_section.cta_subtitle', e.target.value)}
              className="w-full border border-slate-200 px-4 py-2.5 text-[#0F172A] focus:border-[#E07A3A] focus:outline-none" data-testid="landing-cta-subtitle" />
          </div>
        </div>
      </div>

      {/* ===== PROFESSIONAL SECTION ===== */}
      {content.professional_section && (() => {
        const p = content.professional_section;
        return (
          <>
            <div className="border-t-2 border-slate-200 pt-8 mt-4">
              <h2 className="font-['Playfair_Display'] text-2xl text-[#1A1A1A] font-semibold mb-6">Professional Section</h2>
            </div>

            {/* Toggle */}
            <div className="luxury-card p-6 flex items-center justify-between">
              <div>
                <h3 className="font-['Playfair_Display'] text-xl text-[#1A1A1A] font-semibold">2026 Professionals Section</h3>
                <p className="text-sm text-slate-500 mt-1">Toggle visibility of the professional compliance section on the landing page</p>
              </div>
              <button
                onClick={() => updateField('professional_section.enabled', !p.enabled)}
                className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors ${p.enabled ? 'bg-[#E07A3A]' : 'bg-slate-300'}`}
                data-testid="landing-toggle-pro"
              >
                <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${p.enabled ? 'translate-x-8' : 'translate-x-1'}`} />
              </button>
            </div>

            {/* Headlines */}
            <div className="luxury-card p-6">
              <h3 className="font-['Playfair_Display'] text-xl text-[#1A1A1A] font-semibold mb-4">Headlines & Intro</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-xs tracking-wider uppercase text-slate-500 mb-1 font-medium">Badge Text</label>
                  <input type="text" value={p.badge || ''} onChange={e => updateField('professional_section.badge', e.target.value)}
                    className="w-full border border-slate-200 px-4 py-2.5 text-[#0F172A] focus:border-[#E07A3A] focus:outline-none" data-testid="landing-pro-badge" />
                </div>
                <div>
                  <label className="block text-xs tracking-wider uppercase text-slate-500 mb-1 font-medium">Main Headline</label>
                  <input type="text" value={p.headline} onChange={e => updateField('professional_section.headline', e.target.value)}
                    className="w-full border border-slate-200 px-4 py-2.5 text-[#0F172A] focus:border-[#E07A3A] focus:outline-none" data-testid="landing-pro-headline" />
                </div>
                <div>
                  <label className="block text-xs tracking-wider uppercase text-slate-500 mb-1 font-medium">Accent Headline</label>
                  <input type="text" value={p.headline_accent} onChange={e => updateField('professional_section.headline_accent', e.target.value)}
                    className="w-full border border-slate-200 px-4 py-2.5 text-[#0F172A] focus:border-[#E07A3A] focus:outline-none" data-testid="landing-pro-headline-accent" />
                </div>
                <div>
                  <label className="block text-xs tracking-wider uppercase text-slate-500 mb-1 font-medium">Intro Paragraph</label>
                  <textarea value={p.intro} onChange={e => updateField('professional_section.intro', e.target.value)}
                    rows={3} className="w-full border border-slate-200 px-4 py-2.5 text-sm text-[#0F172A] focus:border-[#E07A3A] focus:outline-none" data-testid="landing-pro-intro" />
                </div>
              </div>
            </div>

            {/* Stats */}
            <div className="luxury-card p-6">
              <h3 className="font-['Playfair_Display'] text-xl text-[#1A1A1A] font-semibold mb-4">Stats Row</h3>
              {p.stats?.map((stat, i) => (
                <div key={`item-${i}`} className="border border-slate-100 p-4 mb-4">
                  <div className="grid md:grid-cols-2 gap-4 mb-3">
                    <div>
                      <label className="block text-xs tracking-wider uppercase text-slate-500 mb-1 font-medium">Value</label>
                      <input type="text" value={stat.value} onChange={e => updateField(`professional_section.stats.${i}.value`, e.target.value)}
                        className="w-full border border-slate-200 px-4 py-2.5 text-[#0F172A] focus:border-[#E07A3A] focus:outline-none" data-testid={`landing-pro-stat-${i}-value`} />
                    </div>
                    <div>
                      <label className="block text-xs tracking-wider uppercase text-slate-500 mb-1 font-medium">Label</label>
                      <input type="text" value={stat.label} onChange={e => updateField(`professional_section.stats.${i}.label`, e.target.value)}
                        className="w-full border border-slate-200 px-4 py-2.5 text-[#0F172A] focus:border-[#E07A3A] focus:outline-none" data-testid={`landing-pro-stat-${i}-label`} />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs tracking-wider uppercase text-slate-500 mb-1 font-medium">Description</label>
                    <input type="text" value={stat.description} onChange={e => updateField(`professional_section.stats.${i}.description`, e.target.value)}
                      className="w-full border border-slate-200 px-4 py-2.5 text-sm text-[#0F172A] focus:border-[#E07A3A] focus:outline-none" data-testid={`landing-pro-stat-${i}-desc`} />
                  </div>
                </div>
              ))}
            </div>

            {/* Benefits */}
            <div className="luxury-card p-6">
              <h3 className="font-['Playfair_Display'] text-xl text-[#1A1A1A] font-semibold mb-4">Benefits</h3>
              {p.benefits?.map((benefit, i) => (
                <div key={`item-${i}`} className="border border-slate-100 p-4 mb-4">
                  <label className="block text-xs tracking-wider uppercase text-slate-500 mb-1 font-medium">Benefit {i + 1} Title</label>
                  <input type="text" value={benefit.title} onChange={e => updateField(`professional_section.benefits.${i}.title`, e.target.value)}
                    className="w-full border border-slate-200 px-4 py-2.5 text-[#0F172A] focus:border-[#E07A3A] focus:outline-none mb-3" data-testid={`landing-pro-benefit-${i}-title`} />
                  <label className="block text-xs tracking-wider uppercase text-slate-500 mb-1 font-medium">Description</label>
                  <textarea value={benefit.description} onChange={e => updateField(`professional_section.benefits.${i}.description`, e.target.value)}
                    rows={2} className="w-full border border-slate-200 px-4 py-2.5 text-sm text-[#0F172A] focus:border-[#E07A3A] focus:outline-none" data-testid={`landing-pro-benefit-${i}-desc`} />
                </div>
              ))}
            </div>

            {/* CTA */}
            <div className="luxury-card p-6">
              <h3 className="font-['Playfair_Display'] text-xl text-[#1A1A1A] font-semibold mb-4">Call to Action</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs tracking-wider uppercase text-slate-500 mb-1 font-medium">Button Text</label>
                  <input type="text" value={p.cta_text} onChange={e => updateField('professional_section.cta_text', e.target.value)}
                    className="w-full border border-slate-200 px-4 py-2.5 text-[#0F172A] focus:border-[#E07A3A] focus:outline-none" data-testid="landing-pro-cta-text" />
                </div>
                <div>
                  <label className="block text-xs tracking-wider uppercase text-slate-500 mb-1 font-medium">Subtitle Below Button</label>
                  <input type="text" value={p.cta_subtitle} onChange={e => updateField('professional_section.cta_subtitle', e.target.value)}
                    className="w-full border border-slate-200 px-4 py-2.5 text-[#0F172A] focus:border-[#E07A3A] focus:outline-none" data-testid="landing-pro-cta-subtitle" />
                </div>
              </div>
            </div>
          </>
        );
      })()}

      {/* ===== WHY SHOPPERDEN SECTION ===== */}
      {content.why_shopperden_section && (() => {
        const w = content.why_shopperden_section;
        return (
          <>
            <div className="border-t-2 border-slate-200 pt-8 mt-4">
              <h2 className="font-['Playfair_Display'] text-2xl text-[#1A1A1A] font-semibold mb-6">Why SHOPPERDEN Section</h2>
            </div>

            {/* Toggle */}
            <div className="luxury-card p-6 flex items-center justify-between">
              <div>
                <h3 className="font-['Playfair_Display'] text-xl text-[#1A1A1A] font-semibold">Why SHOPPERDEN + AI Tools</h3>
                <p className="text-sm text-slate-500 mt-1">Savings, no spammers, direct control, and AI listing tools</p>
              </div>
              <button
                onClick={() => updateField('why_shopperden_section.enabled', !w.enabled)}
                className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors ${w.enabled ? 'bg-[#E07A3A]' : 'bg-slate-300'}`}
                data-testid="landing-toggle-why"
              >
                <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${w.enabled ? 'translate-x-8' : 'translate-x-1'}`} />
              </button>
            </div>

            {/* Headlines */}
            <div className="luxury-card p-6">
              <h3 className="font-['Playfair_Display'] text-xl text-[#1A1A1A] font-semibold mb-4">Headlines</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs tracking-wider uppercase text-slate-500 mb-1 font-medium">Main Headline</label>
                  <input type="text" value={w.headline} onChange={e => updateField('why_shopperden_section.headline', e.target.value)}
                    className="w-full border border-slate-200 px-4 py-2.5 text-[#0F172A] focus:border-[#E07A3A] focus:outline-none" data-testid="landing-why-headline" />
                </div>
                <div>
                  <label className="block text-xs tracking-wider uppercase text-slate-500 mb-1 font-medium">Accent Headline</label>
                  <input type="text" value={w.headline_accent} onChange={e => updateField('why_shopperden_section.headline_accent', e.target.value)}
                    className="w-full border border-slate-200 px-4 py-2.5 text-[#0F172A] focus:border-[#E07A3A] focus:outline-none" data-testid="landing-why-headline-accent" />
                </div>
              </div>
            </div>

            {/* Key Points */}
            <div className="luxury-card p-6">
              <h3 className="font-['Playfair_Display'] text-xl text-[#1A1A1A] font-semibold mb-4">Key Points</h3>
              {w.points?.map((point, i) => (
                <div key={`item-${i}`} className="border border-slate-100 p-4 mb-4">
                  <div className="grid md:grid-cols-2 gap-4 mb-3">
                    <div>
                      <label className="block text-xs tracking-wider uppercase text-slate-500 mb-1 font-medium">Title</label>
                      <input type="text" value={point.title} onChange={e => updateField(`why_shopperden_section.points.${i}.title`, e.target.value)}
                        className="w-full border border-slate-200 px-4 py-2.5 text-[#0F172A] focus:border-[#E07A3A] focus:outline-none" data-testid={`landing-why-point-${i}-title`} />
                    </div>
                    <div>
                      <label className="block text-xs tracking-wider uppercase text-slate-500 mb-1 font-medium">Highlight Badge</label>
                      <input type="text" value={point.highlight || ''} onChange={e => updateField(`why_shopperden_section.points.${i}.highlight`, e.target.value)}
                        className="w-full border border-slate-200 px-4 py-2.5 text-[#0F172A] focus:border-[#E07A3A] focus:outline-none" data-testid={`landing-why-point-${i}-highlight`} />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs tracking-wider uppercase text-slate-500 mb-1 font-medium">Description</label>
                    <textarea value={point.description} onChange={e => updateField(`why_shopperden_section.points.${i}.description`, e.target.value)}
                      rows={2} className="w-full border border-slate-200 px-4 py-2.5 text-sm text-[#0F172A] focus:border-[#E07A3A] focus:outline-none" data-testid={`landing-why-point-${i}-desc`} />
                  </div>
                </div>
              ))}
            </div>

            {/* AI Tools Toggle */}
            {w.ai_tools && (
              <div className="luxury-card p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-['Playfair_Display'] text-xl text-[#1A1A1A] font-semibold">AI Tools Sub-Section</h3>
                  <button
                    onClick={() => updateField('why_shopperden_section.ai_tools.enabled', !w.ai_tools.enabled)}
                    className={`relative inline-flex h-6 w-12 items-center rounded-full transition-colors ${w.ai_tools.enabled ? 'bg-[#E07A3A]' : 'bg-slate-300'}`}
                    data-testid="landing-toggle-ai-tools"
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${w.ai_tools.enabled ? 'translate-x-7' : 'translate-x-1'}`} />
                  </button>
                </div>
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs tracking-wider uppercase text-slate-500 mb-1 font-medium">AI Section Headline</label>
                    <input type="text" value={w.ai_tools.headline} onChange={e => updateField('why_shopperden_section.ai_tools.headline', e.target.value)}
                      className="w-full border border-slate-200 px-4 py-2.5 text-[#0F172A] focus:border-[#E07A3A] focus:outline-none" data-testid="landing-ai-headline" />
                  </div>
                  <div>
                    <label className="block text-xs tracking-wider uppercase text-slate-500 mb-1 font-medium">AI Section Description</label>
                    <textarea value={w.ai_tools.description} onChange={e => updateField('why_shopperden_section.ai_tools.description', e.target.value)}
                      rows={2} className="w-full border border-slate-200 px-4 py-2.5 text-sm text-[#0F172A] focus:border-[#E07A3A] focus:outline-none" data-testid="landing-ai-desc" />
                  </div>
                  {w.ai_tools.features?.map((f, i) => (
                    <div key={`feat-${i}`} className="border border-slate-100 p-3">
                      <div className="grid md:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs text-slate-500 mb-1 font-medium">Feature {i+1} Title</label>
                          <input type="text" value={f.title} onChange={e => updateField(`why_shopperden_section.ai_tools.features.${i}.title`, e.target.value)}
                            className="w-full border border-slate-200 px-3 py-2 text-sm text-[#0F172A] focus:border-[#E07A3A] focus:outline-none" data-testid={`landing-ai-feature-${i}-title`} />
                        </div>
                        <div>
                          <label className="block text-xs text-slate-500 mb-1 font-medium">Description</label>
                          <input type="text" value={f.description} onChange={e => updateField(`why_shopperden_section.ai_tools.features.${i}.description`, e.target.value)}
                            className="w-full border border-slate-200 px-3 py-2 text-sm text-[#0F172A] focus:border-[#E07A3A] focus:outline-none" data-testid={`landing-ai-feature-${i}-desc`} />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* CTA */}
            <div className="luxury-card p-6">
              <h3 className="font-['Playfair_Display'] text-xl text-[#1A1A1A] font-semibold mb-4">Call to Action</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs tracking-wider uppercase text-slate-500 mb-1 font-medium">Button Text</label>
                  <input type="text" value={w.cta_text} onChange={e => updateField('why_shopperden_section.cta_text', e.target.value)}
                    className="w-full border border-slate-200 px-4 py-2.5 text-[#0F172A] focus:border-[#E07A3A] focus:outline-none" data-testid="landing-why-cta-text" />
                </div>
                <div>
                  <label className="block text-xs tracking-wider uppercase text-slate-500 mb-1 font-medium">Subtitle</label>
                  <input type="text" value={w.cta_subtitle} onChange={e => updateField('why_shopperden_section.cta_subtitle', e.target.value)}
                    className="w-full border border-slate-200 px-4 py-2.5 text-[#0F172A] focus:border-[#E07A3A] focus:outline-none" data-testid="landing-why-cta-subtitle" />
                </div>
              </div>
            </div>
          </>
        );
      })()}

      {/* Save Button */}
      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-[#E07A3A] hover:bg-[#C4652E] text-white px-10 py-6 text-sm tracking-widest uppercase font-semibold"
          data-testid="landing-save-btn"
        >
          {saving ? 'Saving...' : 'Save & Publish'}
        </Button>
      </div>
    </div>
  );
};
